import { isSafeRedirectPath } from '@kit/shared/utils';

/**
 * Resolve the post-signup redirect for the no-email-confirmation path.
 *
 * Mirrors the logic in `/auth/callback/route.ts` so a dancer who signs up
 * mid-apply-journey lands in the profile-setup wizard with `next` preserved
 * as a query param. The wizard's last step reads that `next` to fire the
 * idempotent `createDraftApplicationFromIntentAction` and skip the user
 * straight to their application.
 *
 *  - dancer signup with safe `next`        → /home/artist/profile/setup?next=<next>
 *  - dancer signup without `next`          → /home/artist/profile/setup
 *  - non-dancer signup with safe `next`    → <next>
 *  - everything else                       → /home
 *
 * Email confirmations are off across local / staging / prod
 * (`enable_confirmations = false` in all three Supabase configs), so this
 * resolver is the only place that wraps the dancer apply-journey URL —
 * the email-token branch of `/auth/callback` never fires in normal flow.
 */
export function resolvePostSignupPath(): string {
  if (typeof window === 'undefined') return '/home';

  const params = new URLSearchParams(window.location.search);
  const rawNext = params.get('next');
  const type = params.get('type');
  const safeNext = rawNext && isSafeRedirectPath(rawNext) ? rawNext : null;

  if (type === 'dancer') {
    return safeNext
      ? `/home/artist/profile/setup?next=${encodeURIComponent(safeNext)}`
      : '/home/artist/profile/setup';
  }

  return safeNext ?? '/home';
}
